import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { usePresenceStore, type CustomStatus, type Presence } from './store';

// A person's own status: GET and PUT /presence/me (COLLABORATION-PLAN.md §7).
// Colleagues' presence never comes from here; it arrives over the live
// connection for the people on screen.

export type Mode = 'auto' | 'away' | 'invisible';

/** A daily do-not-disturb window in IST, "HH:MM" to "HH:MM"; may cross midnight. */
export interface QuietHours {
  from: string;
  until: string;
}

export interface MyStatus {
  mode: Mode;
  dnd_until: string | null;
  status: CustomStatus | null;
  quiet_hours: QuietHours | null;
  presence: Presence;
}

/** Every field, every time: a missing status clears it. */
export interface StatusUpdate {
  mode: Mode;
  dnd_until: string | null;
  status: CustomStatus | null;
  quiet_hours: QuietHours | null;
}

export const presenceKeys = { me: ['presence', 'me'] as const };

export function useMyStatus(enabled = true) {
  const apply = usePresenceStore((s) => s.apply);
  return useQuery({
    queryKey: presenceKeys.me,
    queryFn: async () => {
      const out = await api.get<MyStatus>('/presence/me');
      apply([out.presence]);
      return out;
    },
    enabled,
    retry: false,
  });
}

export function useUpdateMyStatus() {
  const qc = useQueryClient();
  const apply = usePresenceStore((s) => s.apply);
  return useMutation({
    mutationFn: (v: StatusUpdate) => api.put<MyStatus>('/presence/me', v),
    onSuccess: (out) => {
      qc.setQueryData(presenceKeys.me, out);
      // Show the change at once; the live event confirms it moments later.
      apply([out.presence]);
    },
  });
}

/** The current choices as an update, ready to change one thing. */
export function asUpdate(s: MyStatus): StatusUpdate {
  return { mode: s.mode, dnd_until: s.dnd_until, status: s.status, quiet_hours: s.quiet_hours };
}
